import { ALLOWED_FIELDS, type AnalyticsEventName } from "./analytics";
import type { ExperimentArm } from "./types";

const FIREBASE_APP_NAME = "money-rituals-market-test";
const MAX_ANALYTICS_QUEUE_SIZE = 100;
const MAX_COMMENT_LENGTH = 280;
const DEFAULT_FEEDBACK_CONFIRMATION_TIMEOUT_MS = 12_000;

const COMMON_ANALYTICS_FIELDS = {
  schema_version: (value: unknown) => value === 1,
  demo_version: (value: unknown) =>
    typeof value === "string" && /^[a-z0-9._-]{1,40}$/i.test(value),
  arm: inSet("A", "B", "C", "D"),
  source: shortSource,
  is_return_session: isBoolean,
} as const;

const ANALYTICS_FIELD_VALIDATORS: Record<string, (value: unknown) => boolean> = {
  viewport_class: inSet("mobile", "tablet", "desktop"),
  reduced_motion: isBoolean,
  ritual: inSet("frog", "tree"),
  amount_band: inSet("20-49", "50-99", "100-149", "150-200"),
  currency_code: inSet("USD", "UAH"),
  frog_factor: inSet("fixed", "random"),
  tap_count: integerBetween(1, 5),
  enjoy_percent: integerBetween(55, 65),
  duration_band: inSet("under-15s", "15-30s", "30-60s", "over-60s"),
  bubble_index: integerBetween(1, 3),
  confirmed: isBoolean,
  wish_factor: inSet("plain", "tactile"),
  progress_band: inSet(25, 50, 75, 100),
  message_id: shortSlug,
  message_tone: inSet("encouraging"),
  rating: integerBetween(1, 5),
  days_since_prior_band: inSet("same-day", "1-day", "2-6-days", "7-plus-days"),
};

export interface FirebaseBridgeOptions {
  testMode: boolean;
  analyticsEnabled?: boolean;
  feedbackEnabled?: boolean;
  feedbackConfirmationTimeoutMs?: number;
}

export interface FirebaseFeedbackInput {
  rating: number;
  ritual: "frog" | "tree";
  locale: "en" | "uk";
  arm: ExperimentArm;
  source: string;
  demoVersion: string;
  comment?: string;
}

export type SubmitFeedbackResult =
  | { status: "sent" }
  | { status: "disabled" }
  | {
      status: "error";
      code?:
        | "invalid-input"
        | "initialization-failed"
        | "write-failed"
        | "confirmation-timeout";
    };

export interface FirebaseBridge {
  readonly analyticsEnabled: boolean;
  readonly feedbackEnabled: boolean;
  track(record: Readonly<Record<string, unknown>>): void;
  submitFeedback(input: FirebaseFeedbackInput): Promise<SubmitFeedbackResult>;
}

export interface FirebaseTransport {
  logAnalytics(record: SanitizedAnalyticsRecord): void;
  submitFeedback(input: NormalizedFeedbackInput): Promise<void>;
}

export interface FirebaseBridgeDependencies {
  initializeTransport(
    config: ValidFirebaseConfig,
    services: { analyticsEnabled: boolean; feedbackEnabled: boolean },
  ): Promise<FirebaseTransport>;
}

export interface ValidFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  firestoreDatabaseId?: string;
  measurementId?: string;
  messagingSenderId?: string;
  storageBucket?: string;
}

export interface SanitizedAnalyticsRecord extends Record<string, string | number | boolean> {
  event: AnalyticsEventName;
}

export interface NormalizedFeedbackInput {
  rating: number;
  ritual: "frog" | "tree";
  locale: "en" | "uk";
  arm: ExperimentArm;
  source: string;
  demoVersion: string;
  comment?: string;
}

export interface FeedbackDocument {
  schemaVersion: 1;
  rating: number;
  ritual: "frog" | "tree";
  locale: "en" | "uk";
  arm: ExperimentArm;
  source: string;
  demoVersion: string;
  comment?: string;
  submittedAt: unknown;
}

const DEFAULT_DEPENDENCIES: FirebaseBridgeDependencies = {
  initializeTransport: initializeFirebaseTransport,
};

export function createFirebaseBridge(
  config: MoneyRitualsFirebaseConfig | undefined,
  options: FirebaseBridgeOptions,
  dependencies: FirebaseBridgeDependencies = DEFAULT_DEPENDENCIES,
): FirebaseBridge {
  const validConfig = validateFirebaseConfig(config);
  const feedbackEnabled =
    validConfig !== null && !options.testMode && options.feedbackEnabled !== false;
  const analyticsEnabled =
    validConfig !== null &&
    !options.testMode &&
    options.analyticsEnabled !== false &&
    typeof validConfig.measurementId === "string";

  if (!validConfig || options.testMode || (!feedbackEnabled && !analyticsEnabled)) {
    return {
      analyticsEnabled,
      feedbackEnabled,
      track: () => undefined,
      submitFeedback: async () => ({ status: "disabled" }),
    };
  }

  let transport: FirebaseTransport | null = null;
  let initializationFailed = false;
  const analyticsQueue: SanitizedAnalyticsRecord[] = [];
  const feedbackConfirmationTimeoutMs = validFeedbackTimeout(
    options.feedbackConfirmationTimeoutMs,
  );

  const transportPromise = dependencies.initializeTransport(validConfig, {
    analyticsEnabled,
    feedbackEnabled,
  }).then(
    (initializedTransport) => {
      transport = initializedTransport;
      flushAnalyticsQueue(analyticsQueue, initializedTransport);
      return initializedTransport;
    },
    () => {
      initializationFailed = true;
      analyticsQueue.length = 0;
      return null;
    },
  );

  return {
    analyticsEnabled,
    feedbackEnabled,
    track(record) {
      if (!analyticsEnabled) return;
      const sanitizedRecord = sanitizeAnalyticsRecord(record);
      if (!sanitizedRecord) return;

      if (transport) {
        safelyLogAnalytics(transport, sanitizedRecord);
        return;
      }
      if (initializationFailed) return;

      if (analyticsQueue.length === MAX_ANALYTICS_QUEUE_SIZE) {
        analyticsQueue.shift();
      }
      analyticsQueue.push(sanitizedRecord);
    },
    async submitFeedback(input) {
      if (!feedbackEnabled) return { status: "disabled" };
      const normalizedInput = normalizeFeedbackInput(input);
      if (!normalizedInput) {
        return { status: "error", code: "invalid-input" };
      }

      const attempt = (async (): Promise<SubmitFeedbackResult> => {
        const initializedTransport = transport ?? (await transportPromise);
        if (!initializedTransport) {
          return { status: "error", code: "initialization-failed" };
        }

        try {
          await initializedTransport.submitFeedback(normalizedInput);
          return { status: "sent" };
        } catch {
          return { status: "error", code: "write-failed" };
        }
      })();
      const confirmedResult = await resolveBeforeTimeout(
        attempt,
        feedbackConfirmationTimeoutMs,
      );
      return confirmedResult ?? { status: "error", code: "confirmation-timeout" };
    },
  };
}

export function sanitizeAnalyticsRecord(
  record: Readonly<Record<string, unknown>>,
): SanitizedAnalyticsRecord | null {
  const event = record.event;
  if (!isAnalyticsEventName(event)) return null;

  const sanitized: SanitizedAnalyticsRecord = { event };
  for (const [key, validator] of Object.entries(COMMON_ANALYTICS_FIELDS)) {
    const value = record[key];
    if (validator(value) && isSafeAnalyticsValue(value)) {
      sanitized[key] = value;
    }
  }

  for (const key of ALLOWED_FIELDS[event]) {
    const value = record[key];
    if (
      isSafeAnalyticsValue(value) &&
      (ANALYTICS_FIELD_VALIDATORS[key]?.(value) ?? false)
    ) {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

export function normalizeFeedbackInput(
  input: FirebaseFeedbackInput,
): NormalizedFeedbackInput | null {
  if (!isPlainRecord(input)) return null;
  const allowedKeys = new Set([
    "rating",
    "ritual",
    "locale",
    "arm",
    "source",
    "demoVersion",
    "comment",
  ]);
  if (Object.keys(input).some((key) => !allowedKeys.has(key))) return null;
  if (!integerBetween(1, 5)(input.rating)) return null;
  if (!inSet("frog", "tree")(input.ritual)) return null;
  if (!inSet("en", "uk")(input.locale)) return null;
  if (!inSet("A", "B", "C", "D")(input.arm)) return null;
  if (!shortSource(input.source)) return null;
  if (!safeVersion(input.demoVersion)) return null;

  const normalized: NormalizedFeedbackInput = {
    rating: input.rating,
    ritual: input.ritual,
    locale: input.locale,
    arm: input.arm,
    source: input.source,
    demoVersion: input.demoVersion,
  };

  if (input.comment !== undefined) {
    if (typeof input.comment !== "string") return null;
    const comment = input.comment.trim();
    if (
      comment.length > MAX_COMMENT_LENGTH ||
      /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(comment)
    ) {
      return null;
    }
    if (comment) normalized.comment = comment;
  }

  return normalized;
}

export function validateFirebaseConfig(
  config: MoneyRitualsFirebaseConfig | undefined,
): ValidFirebaseConfig | null {
  if (!isPlainRecord(config)) return null;
  if (!boundedString(config.apiKey, 1, 200)) return null;
  if (!hostname(config.authDomain)) return null;
  if (!projectId(config.projectId)) return null;
  if (!appId(config.appId)) return null;

  const validated: ValidFirebaseConfig = {
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    appId: config.appId,
  };

  if (config.measurementId !== undefined) {
    if (typeof config.measurementId !== "string" || !/^G-[A-Z0-9]{4,20}$/.test(config.measurementId)) {
      return null;
    }
    validated.measurementId = config.measurementId;
  }
  if (config.firestoreDatabaseId !== undefined) {
    if (
      typeof config.firestoreDatabaseId !== "string" ||
      !/^[a-z][a-z0-9-]{2,61}[a-z0-9]$/.test(config.firestoreDatabaseId)
    ) {
      return null;
    }
    validated.firestoreDatabaseId = config.firestoreDatabaseId;
  }
  if (config.messagingSenderId !== undefined) {
    if (typeof config.messagingSenderId !== "string" || !/^\d{3,30}$/.test(config.messagingSenderId)) {
      return null;
    }
    validated.messagingSenderId = config.messagingSenderId;
  }
  if (config.storageBucket !== undefined) {
    if (typeof config.storageBucket !== "string" || !hostname(config.storageBucket)) return null;
    validated.storageBucket = config.storageBucket;
  }
  return validated;
}

export function feedbackDocumentSegments(
  uid: string,
  ritual: "frog" | "tree",
): readonly ["feedback", string, "rituals", "frog" | "tree"] {
  return ["feedback", uid, "rituals", ritual];
}

export function buildFeedbackDocument(
  input: NormalizedFeedbackInput,
  submittedAt: unknown,
): FeedbackDocument {
  return {
    schemaVersion: 1,
    rating: input.rating,
    ritual: input.ritual,
    locale: input.locale,
    arm: input.arm,
    source: input.source,
    demoVersion: input.demoVersion,
    ...(input.comment ? { comment: input.comment } : {}),
    submittedAt,
  };
}

async function initializeFirebaseTransport(
  config: ValidFirebaseConfig,
  services: { analyticsEnabled: boolean; feedbackEnabled: boolean },
): Promise<FirebaseTransport> {
  const appModule = await import("firebase/app");
  const existingApp = appModule.getApps().find((app) => app.name === FIREBASE_APP_NAME);
  const app =
    existingApp ??
    appModule.initializeApp(
      {
        apiKey: config.apiKey,
        authDomain: config.authDomain,
        projectId: config.projectId,
        appId: config.appId,
        measurementId: config.measurementId,
        messagingSenderId: config.messagingSenderId,
        storageBucket: config.storageBucket,
      },
      FIREBASE_APP_NAME,
    );

  let emitAnalytics: ((record: SanitizedAnalyticsRecord) => void) | null = null;
  if (services.analyticsEnabled && config.measurementId) {
    try {
      const analyticsModule = await import("firebase/analytics");
      if (await analyticsModule.isSupported()) {
        const analytics = analyticsModule.getAnalytics(app);
        emitAnalytics = (record) => {
          const { event, ...parameters } = record;
          analyticsModule.logEvent(analytics, event, parameters);
        };
      }
    } catch {
      emitAnalytics = null;
    }
  }

  let writeFeedback: ((input: NormalizedFeedbackInput) => Promise<void>) | null = null;
  if (services.feedbackEnabled) {
    const [authModule, firestoreModule] = await Promise.all([
      import("firebase/auth"),
      import("firebase/firestore"),
    ]);
    const auth = authModule.getAuth(app);
    const firestore = config.firestoreDatabaseId
      ? firestoreModule.getFirestore(app, config.firestoreDatabaseId)
      : firestoreModule.getFirestore(app);
    writeFeedback = async (input) => {
      const user = auth.currentUser ?? (await authModule.signInAnonymously(auth)).user;
      const feedbackReference = firestoreModule.doc(
        firestore,
        ...feedbackDocumentSegments(user.uid, input.ritual),
      );
      await firestoreModule.setDoc(
        feedbackReference,
        buildFeedbackDocument(input, firestoreModule.serverTimestamp()),
      );
    };
  }

  return {
    logAnalytics(record) {
      emitAnalytics?.(record);
    },
    async submitFeedback(input) {
      if (!writeFeedback) throw new Error("Firebase feedback is disabled.");
      await writeFeedback(input);
    },
  };
}

function flushAnalyticsQueue(
  queue: SanitizedAnalyticsRecord[],
  transport: FirebaseTransport,
): void {
  for (const record of queue.splice(0)) {
    safelyLogAnalytics(transport, record);
  }
}

function safelyLogAnalytics(
  transport: FirebaseTransport,
  record: SanitizedAnalyticsRecord,
): void {
  try {
    transport.logAnalytics(record);
  } catch {
    // Analytics must never interrupt the ritual.
  }
}

function validFeedbackTimeout(value: number | undefined): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 60_000
    ? value
    : DEFAULT_FEEDBACK_CONFIRMATION_TIMEOUT_MS;
}

function resolveBeforeTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = globalThis.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, timeoutMs);

    void promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timeout);
        resolve(value);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

function isAnalyticsEventName(value: unknown): value is AnalyticsEventName {
  return typeof value === "string" && Object.hasOwn(ALLOWED_FIELDS, value);
}

function isSafeAnalyticsValue(value: unknown): value is string | number | boolean {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return value.length <= 100;
  return typeof value === "boolean";
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value) as unknown;
  return prototype === Object.prototype || prototype === null;
}

function boundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && value.length >= minimum && value.length <= maximum;
}

function hostname(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= 253 &&
    /^(?=.{1,253}$)(?!-)[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\.?$)/i.test(value)
  );
}

function projectId(value: unknown): value is string {
  return typeof value === "string" && /^[a-z][a-z0-9-]{3,62}[a-z0-9]$/.test(value);
}

function appId(value: unknown): value is string {
  return typeof value === "string" && /^[a-z0-9:._-]{5,200}$/i.test(value);
}

function shortSource(value: unknown): value is string {
  return typeof value === "string" && /^[a-z0-9_-]{1,40}$/.test(value);
}

function shortSlug(value: unknown): value is string {
  return typeof value === "string" && /^[a-z0-9-]{1,40}$/.test(value);
}

function safeVersion(value: unknown): value is string {
  return typeof value === "string" && /^[a-z0-9._-]{1,40}$/i.test(value);
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function integerBetween(minimum: number, maximum: number): (value: unknown) => value is number {
  return (value: unknown): value is number =>
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= minimum &&
    value <= maximum;
}

function inSet<T extends string | number>(
  ...allowed: readonly T[]
): (value: unknown) => value is T {
  return (value: unknown): value is T => allowed.includes(value as T);
}
